class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector("details");
    this.content =
      this.mainDetailsToggle.querySelector("summary").nextElementSibling;

    this.mainDetailsToggle.addEventListener(
      "focusout",
      this.onFocusOut.bind(this)
    );
    this.mainDetailsToggle.addEventListener("toggle", this.onToggle.bind(this));
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onToggle() {
    if (!this.animations) this.animations = this.content.getAnimations();

    const summary = this.mainDetailsToggle.querySelector("summary");
    if (this.mainDetailsToggle.hasAttribute("open")) {
      this.animations.forEach((animation) => animation.play());
      if (summary) {
        summary.setAttribute("aria-expanded", "true");
      }
      this.mainDetailsToggle.setAttribute("aria-expanded", "true");
    } else {
      this.animations.forEach((animation) => animation.cancel());
      if (summary) {
        summary.setAttribute("aria-expanded", "false");
      }
      this.mainDetailsToggle.setAttribute("aria-expanded", "false");
    }
  }

  close() {
    this.mainDetailsToggle.removeAttribute("open");
    this.mainDetailsToggle
      .querySelector("summary")
      .setAttribute("aria-expanded", false);
    this.mainDetailsToggle.setAttribute("aria-expanded", "false");
  }
}

customElements.define("details-disclosure", DetailsDisclosure);

class HeaderMenu extends DetailsDisclosure {
  static activeScrollManager = null;

  constructor() {
    super();
    this.header = document.querySelector(".header-wrapper");
    this.isAnimatingClose = false;
    this.isMenuOpen = false;
    this.shouldAnimateClose = false;

    const summary = this.mainDetailsToggle.querySelector("summary");
    summary.addEventListener("click", this.onSummaryClick.bind(this), {
      capture: true,
    });
    summary.addEventListener(
      "pointerdown",
      (e) => {
        const isThisOpen = this.mainDetailsToggle.hasAttribute("open");
        if (isThisOpen) return;
        const openMenus = Array.from(
          document.querySelectorAll(".mega-menu[open]")
        ).filter((menu) => menu !== this.mainDetailsToggle);
        if (openMenus.length === 0) return;
        openMenus.forEach((menu) => menu.classList.add("menu-no-animation"));
        this.mainDetailsToggle.classList.add("menu-no-animation");
      },
      { capture: true }
    );

    const overlay = this.mainDetailsToggle.querySelector(".mega-menu__overlay");
    if (overlay) {
      overlay.addEventListener("click", this.onOverlayClick.bind(this));
    }

    this.addEventListener("click", (e) => {
      if (this.isMenuOpen && e.target.closest(".mega-menu__link--level-2")) {
        setTimeout(() => this.enforceScrollLock(), 10);
      }
    });
  }

  onSummaryClick(event) {
    event.preventDefault();
    if (typeof event.stopImmediatePropagation === "function") {
      event.stopImmediatePropagation();
    } else {
      event.stopPropagation();
    }

    if (this.mainDetailsToggle.hasAttribute("open")) {
      this.shouldAnimateClose = true;
      this.animateClose();
    } else {
      const openMenus = Array.from(
        document.querySelectorAll(".mega-menu[open]")
      ).filter((menu) => menu !== this.mainDetailsToggle);

      if (openMenus.length > 0) {
        this.openWithoutAnimation(openMenus);
      } else {
        this.animateOpen();
      }
    }
  }

  onOverlayClick(event) {
    event.preventDefault();
    event.stopPropagation();
    this.shouldAnimateClose = true;
    this.animateClose();
  }

  animateOpen() {
    this.isMenuOpen = true;

    if (!HeaderMenu.activeScrollManager) {
      HeaderMenu.activeScrollManager = this;

      this.scrollPosition = window.pageYOffset || window.scrollY;

      if (window.scrollTrigger) {
        window.scrollTrigger.disable();
      }

      const useBodyScrollLock =
        typeof window !== "undefined" &&
        window.bodyScrollLock &&
        typeof window.bodyScrollLock.lock === "function" &&
        typeof window.bodyScrollLock.unlock === "function";

      if (useBodyScrollLock) {
        this.scrollLockBreakpoint =
          window.matchMedia && window.matchMedia("(min-width: 990px)").matches
            ? "desktop"
            : "tablet";
        window.bodyScrollLock.lock(this.scrollLockBreakpoint);
      } else {
        const scrollbarWidth =
          window.innerWidth - document.documentElement.clientWidth;

        this.originalBodyPosition = document.body.style.position;
        this.originalBodyTop = document.body.style.top;
        this.originalBodyWidth = document.body.style.width;
        this.originalBodyHeight = document.body.style.height;
        this.originalBodyPaddingRight = document.body.style.paddingRight;

        document.body.style.position = "fixed";
        document.body.style.top = `-${this.scrollPosition}px`;
        document.body.style.width = "100%";
        document.body.style.height = "100%";

        if (scrollbarWidth > 0) {
          document.body.style.paddingRight = `${scrollbarWidth}px`;
        }

        this.touchMoveHandler = (e) => {
          if (!e.target.closest(".mega-menu__panel")) {
            e.preventDefault();
          }
        };
        document.addEventListener("touchmove", this.touchMoveHandler, {
          passive: false,
        });
      }
    }

    this.mainDetailsToggle.setAttribute("open", "");
    this.mainDetailsToggle.classList.remove("menu-opening");
    this.mainDetailsToggle
      .querySelector("summary")
      .setAttribute("aria-expanded", "true");
    this.mainDetailsToggle.setAttribute("aria-expanded", "true");
    this.setAttribute("aria-expanded", "true");
  }

  openWithoutAnimation(otherOpenMenus = []) {
    this.isMenuOpen = true;

    const previousManager = HeaderMenu.activeScrollManager;
    if (previousManager && previousManager !== this) {
      this.scrollPosition = previousManager.scrollPosition;
      this.scrollLockBreakpoint = previousManager.scrollLockBreakpoint;
      HeaderMenu.activeScrollManager = this;
      previousManager.isMenuOpen = false;
    } else if (!HeaderMenu.activeScrollManager) {
      if (window.ScrollTrigger) {
        window.ScrollTrigger.disable();
      }
    }
    const cancelAnimations = (root) => {
      if (!root) return;
      const parts = [
        root.querySelector(".mega-menu__content"),
        root.querySelector(".mega-menu__content > .content-drawer"),
        root.querySelector(".mega-menu__content > .mega-menu__overlay"),
        root.querySelector(".mega-menu__content > .mega-menu__panel"),
        root.querySelector(".mega-menu__panel-inner"),
      ].filter(Boolean);
      parts.forEach((el) => {
        try {
          el.getAnimations?.().forEach((anim) => anim.cancel());
          const prev = el.style.animation;
          el.style.animation = "none";
          setTimeout(() => {
            el.style.animation = prev || "";
          }, 80);
        } catch (_) {}
      });
    };

    const applyInstantStyles = (root) => {
      if (!root) return;
      const content = root.querySelector(".mega-menu__content");
      const drawer = root.querySelector(
        ".mega-menu__content > .content-drawer"
      );
      const overlay = root.querySelector(
        ".mega-menu__content > .mega-menu__overlay"
      );
      const panel = root.querySelector(
        ".mega-menu__content > .mega-menu__panel"
      );
      const panelInner = root.querySelector(".mega-menu__panel-inner");

      if (content) {
        content.style.transition = "none";
        content.style.opacity = "1";
      }
      if (drawer) {
        drawer.style.transition = "none";
        drawer.style.animation = "none";
        drawer.style.transform = "translateX(0)";
      }
      if (overlay) {
        overlay.style.transition = "none";
        overlay.style.animation = "none";
        overlay.style.opacity = "0.6";
      }
      if (panel) {
        panel.style.transition = "none";
        panel.style.animation = "none";
        panel.style.opacity = "1";
      }
      if (panelInner) {
        panelInner.style.transition = "none";
        panelInner.style.animation = "none";
        panelInner.style.opacity = "1";
        panelInner.style.transform = "none";
      }
    };

    const clearInstantStyles = (root) => {
      if (!root) return;
      const content = root.querySelector(".mega-menu__content");
      const drawer = root.querySelector(
        ".mega-menu__content > .content-drawer"
      );
      const overlay = root.querySelector(
        ".mega-menu__content > .mega-menu__overlay"
      );
      const panel = root.querySelector(
        ".mega-menu__content > .mega-menu__panel"
      );
      const panelInner = root.querySelector(".mega-menu__panel-inner");

      if (content) {
        content.style.transition = "";
        content.style.opacity = "";
      }
      if (drawer) {
        drawer.style.transition = "";
        drawer.style.animation = "";
        drawer.style.transform = "";
      }
      if (overlay) {
        overlay.style.transition = "";
        overlay.style.animation = "";
        overlay.style.opacity = "";
      }
      if (panel) {
        panel.style.transition = "";
        panel.style.animation = "";
        panel.style.opacity = "";
      }
      if (panelInner) {
        panelInner.style.transition = "";
        panelInner.style.animation = "";
        panelInner.style.opacity = "";
        panelInner.style.transform = "";
      }
    };

    const forceVisible = (menu) => menu.classList.add("menu-force-visible");
    const forceHidden = (menu) => menu.classList.add("menu-force-hidden");
    const clearForces = (menu) => {
      menu.classList.remove("menu-force-visible");
      menu.classList.remove("menu-force-hidden");
    };

    otherOpenMenus.forEach((menu) => {
      menu.classList.add("menu-no-animation");
      menu.classList.remove("menu-opening");
      cancelAnimations(menu);
      applyInstantStyles(menu);
      forceHidden(menu);
      const summary = menu.querySelector("summary");
      menu.removeAttribute("open");
      summary && summary.setAttribute("aria-expanded", "false");
      menu.setAttribute("aria-expanded", "false");
      const headerMenuElement = menu.closest("header-menu");
      if (headerMenuElement) {
        headerMenuElement.setAttribute("aria-expanded", "false");
      }
      const panel = menu.querySelector("[data-mega-panel]");
      if (panel) panel.innerHTML = "";
    });

    requestAnimationFrame(() => {
      this.mainDetailsToggle.classList.add("menu-no-animation");
      this.mainDetailsToggle.classList.remove("menu-opening");
      cancelAnimations(this.mainDetailsToggle);
      applyInstantStyles(this.mainDetailsToggle);
      forceVisible(this.mainDetailsToggle);
      this.mainDetailsToggle.setAttribute("open", "");
      this.mainDetailsToggle
        .querySelector("summary")
        .setAttribute("aria-expanded", "true");
      this.mainDetailsToggle.setAttribute("aria-expanded", "true");
      this.setAttribute("aria-expanded", "true");
    });

    requestAnimationFrame(() => {
      otherOpenMenus.forEach((menu) => {
        menu.classList.remove("menu-no-animation");
        clearForces(menu);
        clearInstantStyles(menu);
      });
      clearForces(this.mainDetailsToggle);
      clearInstantStyles(this.mainDetailsToggle);
    });
  }

  enforceScrollLock() {
    if (!this.isMenuOpen || this.scrollPosition === undefined) return;
    document.body.style.width = "100%";
    document.body.style.height = "100%";

    const scrollbarWidth =
      window.innerWidth - document.documentElement.clientWidth;
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }
  }

  animateClose() {
    if (this.isAnimatingClose) return;

    this.isAnimatingClose = true;
    this.isMenuOpen = false;
    if (this.shouldAnimateClose) {
      this.mainDetailsToggle.classList.remove("menu-no-animation");

      document.querySelectorAll(".header__menu-item").forEach((item) => {
        item.style.color = "var(--color-secondary)";
      });
    }
    this.mainDetailsToggle.classList.add("menu-opening");

    setTimeout(() => {
      this.mainDetailsToggle.removeAttribute("open");
      this.mainDetailsToggle.classList.remove("menu-opening");
      this.mainDetailsToggle
        .querySelector("summary")
        .setAttribute("aria-expanded", "false");
      this.mainDetailsToggle.setAttribute("aria-expanded", "false");
      this.setAttribute("aria-expanded", "false");
      this.isAnimatingClose = false;
      this.shouldAnimateClose = false;

      if (!document.querySelector(".mega-menu[open]")) {
        document
          .querySelectorAll(".mega-menu.menu-no-animation")
          .forEach((el) => el.classList.remove("menu-no-animation"));
      }

      if (HeaderMenu.activeScrollManager === this) {
        const useBodyScrollLock =
          typeof window !== "undefined" &&
          window.bodyScrollLock &&
          typeof window.bodyScrollLock.unlock === "function";

        if (useBodyScrollLock && this.scrollLockBreakpoint) {
          window.bodyScrollLock.unlock(this.scrollLockBreakpoint);
        } else {
          document.body.style.position = this.originalBodyPosition || "";
          document.body.style.top = this.originalBodyTop || "";
          document.body.style.width = this.originalBodyWidth || "";
          document.body.style.height = this.originalBodyHeight || "";
          document.body.style.paddingRight =
            this.originalBodyPaddingRight || "";

          if (this.touchMoveHandler) {
            document.removeEventListener("touchmove", this.touchMoveHandler);
            this.touchMoveHandler = null;
          }
        }

        window.scrollTo(0, this.scrollPosition);

        if (window.ScrollTrigger) {
          requestAnimationFrame(() => {
            window.ScrollTrigger.enable();
            window.ScrollTrigger.refresh();
          });
        }

        this.scrollLockBreakpoint = null;

        HeaderMenu.activeScrollManager = null;
      }
    }, 400);
  }

  onToggle() {
    if (!this.header) return;
    this.header.preventHide = this.mainDetailsToggle.open;

    if (
      document.documentElement.style.getPropertyValue(
        "--header-bottom-position-desktop"
      ) !== ""
    )
      return;
    document.documentElement.style.setProperty(
      "--header-bottom-position-desktop",
      `${Math.floor(this.header.getBoundingClientRect().bottom)}px`
    );
  }

  close() {
    this.animateClose();
  }
}

customElements.define("header-menu", HeaderMenu);
